// Informes (docs/108 B9). Cada módulo del menú Informes muestra su catálogo (a la izquierda); se elige
// un informe, se dan sus parámetros (período, ubicación, días) y el backend lo ejecuta: columnas
// tipadas, filas y totales. Se exporta a CSV (Excel) o se imprime. Nada se calcula aquí.
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api } from '../api/cliente';
import { PanelError } from '../comun/Estados';
import { EncabezadoImpreso, Imprimir } from '../comun/Imprimir';
import { decimal, fecha, pesos } from '../comun/formato';
import { hoy } from '../compras/comun';
import { useSesion } from '../sesion/SesionContexto';
import { SelectorBodega, useBodegas } from '../stock/PantallasStock';

interface ColumnaInforme {
  id: string;
  titulo: string;
  tipo: 'texto' | 'entero' | 'pesos' | 'cantidad' | 'fecha' | 'porcentaje';
  suma?: boolean;
}
interface DefinicionInforme {
  id: string;
  modulo: string;
  nombre: string;
  descripcion: string;
  parametros: Array<'rango' | 'ubicacion' | 'dias'>;
  columnas: ColumnaInforme[];
}
interface ResultadoInforme {
  informe: DefinicionInforme;
  parametros: { desde: string; hasta: string; dias: number; ubicacionId?: string };
  columnas: ColumnaInforme[];
  filas: Array<Record<string, unknown>>;
  totales: Record<string, string>;
  generadoEn: string;
}

function valor(col: ColumnaInforme, v: unknown): string {
  if (v === null || v === undefined || v === '') return '';
  const t = String(v);
  switch (col.tipo) {
    case 'pesos':
      return pesos(t);
    case 'cantidad':
    case 'entero':
      return decimal(t);
    case 'fecha':
      return fecha(t);
    case 'porcentaje':
      return `${decimal(t)} %`;
    default:
      return t;
  }
}

/** CSV con punto y coma (Excel en español) y BOM, cifras sin formato (para poder sumar). */
function aCsv(r: ResultadoInforme): string {
  const esc = (x: unknown) => {
    const s = x === null || x === undefined ? '' : String(x);
    return /[;"\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const filas = [
    r.columnas.map((c) => esc(c.titulo)).join(';'),
    ...r.filas.map((f) =>
      r.columnas
        .map((c) => {
          const v = f[c.id];
          return esc(
            c.tipo === 'texto' || c.tipo === 'fecha' ? v : String(v ?? '').replace('.', ','),
          );
        })
        .join(';'),
    ),
  ];
  return `${String.fromCharCode(0xfeff)}${filas.join('\r\n')}`;
}

function descargarCsv(r: ResultadoInforme) {
  const b = new Blob([aCsv(r)], { type: 'text/csv;charset=utf-8' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(b);
  a.download = `${r.informe.id}-${r.parametros.desde}-${r.parametros.hasta}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

function TablaInforme({ r, impresa = false }: { r: ResultadoInforme; impresa?: boolean }) {
  const der = (c: ColumnaInforme) => c.tipo !== 'texto' && c.tipo !== 'fecha';
  return (
    <table className={impresa ? 'impreso-tabla' : 'grilla compacta'} aria-label={r.informe.nombre}>
      <thead>
        <tr>
          {r.columnas.map((c) => (
            <th key={c.id} className={der(c) ? 'der' : undefined}>
              {c.titulo}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {r.filas.map((f, i) => (
          <tr key={i}>
            {r.columnas.map((c) => (
              <td key={c.id} className={der(c) ? 'der' : undefined}>
                {valor(c, f[c.id])}
              </td>
            ))}
          </tr>
        ))}
        {!r.filas.length && (
          <tr>
            <td colSpan={r.columnas.length} className="grilla-vacia">
              Sin datos para estos parámetros
            </td>
          </tr>
        )}
      </tbody>
      {Object.keys(r.totales).length > 0 && r.filas.length > 0 && (
        <tfoot>
          <tr>
            {r.columnas.map((c, i) => (
              <td key={c.id} className={der(c) ? 'der' : undefined}>
                {i === 0 ? (
                  <b>Total</b>
                ) : r.totales[c.id] !== undefined ? (
                  <b>{valor(c, r.totales[c.id])}</b>
                ) : (
                  ''
                )}
              </td>
            ))}
          </tr>
        </tfoot>
      )}
    </table>
  );
}

export function Informes({ modulo }: { modulo: string }) {
  const { empresa } = useSesion();
  const empresaId = empresa!.id;
  const bodegas = useBodegas(empresaId);
  const catalogo = useQuery({
    queryKey: ['informes', 'catalogo', modulo],
    queryFn: () => api.get<DefinicionInforme[]>('/informes/catalogo', { query: { modulo } }),
    staleTime: 10 * 60_000,
  });
  const [sel, setSel] = useState<string | null>(null);
  const def = (catalogo.data ?? []).find((d) => d.id === sel) ?? catalogo.data?.[0] ?? null;
  useEffect(() => {
    if (!sel && catalogo.data?.[0]) setSel(catalogo.data[0].id);
  }, [sel, catalogo.data]);
  const [desde, setDesde] = useState(`${hoy().slice(0, 8)}01`);
  const [hasta, setHasta] = useState(hoy());
  const [ubicacion, setUbicacion] = useState('');
  const [dias, setDias] = useState('60');
  const [pedido, setPedido] = useState<Record<string, string> | null>(null);
  const [imprimir, setImprimir] = useState(false);
  const r = useQuery({
    queryKey: ['emp', empresaId, 'informe', def?.id, pedido],
    queryFn: () =>
      api.get<ResultadoInforme>(`/informes/${def!.id}`, { query: { empresaId, ...pedido } }),
    enabled: !!def && !!pedido,
  });
  const generar = () => {
    if (!def) return;
    setPedido({
      ...(def.parametros.includes('rango') ? { desde, hasta } : {}),
      ...(def.parametros.includes('ubicacion') && ubicacion ? { ubicacionId: ubicacion } : {}),
      ...(def.parametros.includes('dias') && /^\d+$/.test(dias) ? { dias } : {}),
    });
  };
  return (
    <div className="pantalla consulta informes" data-pantalla={`informes-${modulo}`}>
      {catalogo.isError && <PanelError error={catalogo.error} />}
      <div className="cuerpo-od">
        <nav aria-label="Informes disponibles">
          <ul className="lista-simple">
            {(catalogo.data ?? []).map((d) => (
              <li key={d.id}>
                <button
                  type="button"
                  className={d.id === def?.id ? 'primario' : 'enlace'}
                  aria-current={d.id === def?.id}
                  onClick={() => {
                    setSel(d.id);
                    setPedido(null);
                  }}
                >
                  {d.nombre}
                </button>
              </li>
            ))}
          </ul>
        </nav>
        {def && (
          <section aria-label={def.nombre}>
            <h3>{def.nombre}</h3>
            <p className="apagado">{def.descripcion}</p>
            <div className="barra-filtros">
              {def.parametros.includes('rango') && (
                <>
                  <label className="campo">
                    <span className="etiqueta">Desde</span>
                    <input
                      type="date"
                      aria-label="Desde"
                      value={desde}
                      onChange={(e) => setDesde(e.target.value)}
                    />
                  </label>
                  <label className="campo">
                    <span className="etiqueta">Hasta</span>
                    <input
                      type="date"
                      aria-label="Hasta"
                      value={hasta}
                      onChange={(e) => setHasta(e.target.value)}
                    />
                  </label>
                </>
              )}
              {def.parametros.includes('ubicacion') && (
                <SelectorBodega
                  bodegas={bodegas.lista}
                  valor={ubicacion}
                  alCambiar={setUbicacion}
                  conTodas
                />
              )}
              {def.parametros.includes('dias') && (
                <label className="campo campo-corto">
                  <span className="etiqueta">Días</span>
                  <input
                    aria-label="Días"
                    inputMode="numeric"
                    value={dias}
                    onChange={(e) => setDias(e.target.value)}
                  />
                </label>
              )}
              <button type="button" className="primario" onClick={generar}>
                Generar
              </button>
              {r.data && (
                <>
                  <button type="button" onClick={() => descargarCsv(r.data!)}>
                    Exportar CSV
                  </button>
                  <button type="button" onClick={() => setImprimir(true)}>
                    Imprimir
                  </button>
                </>
              )}
            </div>
            {r.isError && <PanelError error={r.error} />}
            {r.isFetching && <p className="apagado">Generando…</p>}
            {r.data && !r.isFetching && (
              <>
                <p className="apagado">
                  {r.data.filas.length} filas
                  {def.parametros.includes('rango')
                    ? ` · del ${fecha(r.data.parametros.desde)} al ${fecha(r.data.parametros.hasta)}`
                    : ''}
                </p>
                <TablaInforme r={r.data} />
              </>
            )}
          </section>
        )}
      </div>
      {imprimir && r.data && (
        <Imprimir alTerminar={() => setImprimir(false)}>
          <article className="impreso">
            <EncabezadoImpreso
              empresa={empresa!.razon_social}
              titulo={r.data.informe.nombre}
              numero={
                r.data.informe.parametros.includes('rango')
                  ? `${fecha(r.data.parametros.desde)} – ${fecha(r.data.parametros.hasta)}`
                  : ''
              }
            />
            <TablaInforme r={r.data} impresa />
          </article>
        </Imprimir>
      )}
    </div>
  );
}
